// ============================================================================
// TOOLTIP MANAGER
// ----------------------------------------------------------------------------
// Global mouseenter/mouseleave listener that renders a single portal-based
// tooltip for any element carrying a `data-tooltip` attribute anywhere in
// the app. Replaces the old CSS `::before`/`::after` pseudo-element approach
// which was clipped whenever the host sat inside a scrollable ancestor
// (e.g. the Stage 3 scope-builder left column has `overflow-y: auto` which
// clips tooltips extending above/below their buttons).
//
// The tooltip is portaled to document.body with `position: fixed`, so it
// escapes every parent's overflow/transform stacking context, and it auto-
// flips (top → bottom, left → right) on viewport collision so it always
// stays on screen regardless of where the host sits.
//
// Respects existing attributes:
//   data-tooltip="..."              - the text to show (required)
//   data-tooltip-position="bottom"  - override default "top" placement
//   data-tooltip-type="info|warning|danger|success" - colour variant (optional)
//
// Mount once at the root of the app:
//   <TooltipManager />
// ============================================================================

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const GAP = 10;        // pixels between host and tooltip
const MAX_WIDTH = 320; // hard cap matches the old CSS value
const SHOW_DELAY = 250; // ms before showing on hover (prevents flicker on quick mouse passes)

interface TooltipState {
    text: string;
    rect: DOMRect;
    position: string;
    variant: string;
}

export default function TooltipManager() {
    const [state, setState] = useState<TooltipState | null>(null);
    // refs so the event handlers always have the current value without being
    // re-registered on every state change
    const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const currentHostRef = useRef<Element | null>(null);

    useEffect(() => {
        const clearShowTimer = () => {
            if (showTimerRef.current) {
                clearTimeout(showTimerRef.current);
                showTimerRef.current = null;
            }
        };

        const onOver = (e: any) => {
            const el = e.target && e.target.closest ? e.target.closest('[data-tooltip]') : null;
            if (!el) return;
            const text = el.getAttribute('data-tooltip');
            if (!text || !text.trim()) return;
            // If we're moving over the same host, ignore repeat fires
            if (currentHostRef.current === el) return;
            currentHostRef.current = el;
            clearShowTimer();
            showTimerRef.current = setTimeout(() => {
                // Re-check the host is still in the DOM and still the same
                if (currentHostRef.current !== el || !el.isConnected) return;
                const rect = el.getBoundingClientRect();
                // Bail if the host has zero size (hidden)
                if (rect.width === 0 && rect.height === 0) return;
                const position = el.getAttribute('data-tooltip-position') || 'top';
                const variant = el.getAttribute('data-tooltip-type') || 'default';
                setState({ text, rect, position, variant });
            }, SHOW_DELAY);
        };

        const onOut = (e: any) => {
            const el = e.target && e.target.closest ? e.target.closest('[data-tooltip]') : null;
            if (!el) return;
            // Only hide if the mouse actually left the host (not into a child)
            const relatedInside = e.relatedTarget && el.contains && el.contains(e.relatedTarget);
            if (relatedInside) return;
            if (currentHostRef.current === el) {
                currentHostRef.current = null;
            }
            clearShowTimer();
            setState(null);
        };

        // Hide on scroll (anywhere) - the tooltip is position:fixed so its
        // coordinates are stale once the scroll origin moves.
        const onScroll = () => {
            if (currentHostRef.current) {
                currentHostRef.current = null;
            }
            clearShowTimer();
            setState(null);
        };

        // Hide on key press - escapes stuck tooltips when the user is typing.
        const onKey = (e: any) => {
            if (e.key === 'Escape' || e.key === 'Tab') {
                if (currentHostRef.current) currentHostRef.current = null;
                clearShowTimer();
                setState(null);
            }
        };

        document.addEventListener('mouseover', onOver);
        document.addEventListener('mouseout', onOut);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('keydown', onKey);
        return () => {
            clearShowTimer();
            document.removeEventListener('mouseover', onOver);
            document.removeEventListener('mouseout', onOut);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('keydown', onKey);
        };
    }, []);

    if (!state) return null;
    const { text, rect, position, variant } = state;

    // Compute an initial position based on the requested placement, then clamp
    // to the viewport so the tooltip never spills off-screen.
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

    // Measure the tooltip text width crudely (we can't get the real rect
    // pre-render, so estimate from char count; the max-width cap keeps us
    // from running off-screen on very long strings).
    const estimatedWidth = Math.min(MAX_WIDTH, Math.max(120, text.length * 7 + 28));
    const estimatedHeight = Math.ceil(estimatedWidth / 2 > 0 ? 42 : 42); // 1-2 lines

    // Place top/bottom/left/right relative to host rect
    let placement = position;
    // Flip top → bottom if insufficient space above
    if (placement === 'top' && rect.top - estimatedHeight - GAP < 8) {
        placement = 'bottom';
    } else if (placement === 'bottom' && rect.bottom + estimatedHeight + GAP > vh - 8) {
        placement = 'top';
    }

    let top = 0;
    let left = 0;
    if (placement === 'top') {
        top = rect.top - estimatedHeight - GAP;
        left = rect.left + rect.width / 2 - estimatedWidth / 2;
    } else if (placement === 'bottom') {
        top = rect.bottom + GAP;
        left = rect.left + rect.width / 2 - estimatedWidth / 2;
    } else if (placement === 'left') {
        top = rect.top + rect.height / 2 - estimatedHeight / 2;
        left = rect.left - estimatedWidth - GAP;
    } else if (placement === 'right') {
        top = rect.top + rect.height / 2 - estimatedHeight / 2;
        left = rect.right + GAP;
    }

    // Horizontal clamp - keep 8px margin from either viewport edge
    if (left < 8) left = 8;
    if (left + estimatedWidth > vw - 8) left = vw - 8 - estimatedWidth;
    // Vertical clamp
    if (top < 8) top = 8;
    if (top + estimatedHeight > vh - 8) top = vh - 8 - estimatedHeight;

    // Variant palette - matches the old CSS variants
    const palettes: Record<string, { bg: string; border: string }> = {
        default: { bg: 'linear-gradient(145deg, #1a1a1a 0%, #252525 50%, #1f1f1f 100%)', border: 'var(--qm-accent)' },
        info:    { bg: 'linear-gradient(145deg, #0c2d5a 0%, #13407f 50%, #0f3468 100%)', border: '#3b82f6' },
        warning: { bg: 'linear-gradient(145deg, #4a2a08 0%, #71400d 50%, #5a3209 100%)', border: '#f59e0b' },
        success: { bg: 'linear-gradient(145deg, #0b3c24 0%, #115936 50%, #0d472c 100%)', border: '#10b981' },
        danger:  { bg: 'linear-gradient(145deg, #4a0f0f 0%, #6d1717 50%, #5a1212 100%)', border: '#ef4444' },
    };
    const palette = palettes[variant] || palettes.default!;

    return createPortal(
        <div
            role="tooltip"
            style={{
                position: 'fixed',
                top,
                left,
                maxWidth: MAX_WIDTH,
                padding: '10px 14px',
                background: palette.bg,
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 1.5,
                letterSpacing: '0.01em',
                borderRadius: '8px',
                border: `2px solid ${palette.border}`,
                boxShadow: '0 14px 32px rgba(0,0,0,0.4), 0 6px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.05) inset',
                backdropFilter: 'blur(6px)',
                pointerEvents: 'none',
                zIndex: 2147483647,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
            }}
        >
            {text}
        </div>,
        document.body,
    );
}
